import * as React from 'react'
import type { Answer } from 'types/answer'
import type { Question } from 'types/question'

import ZentakeFormContext from './ZentakeFormContext'

type RepeatSectionResponse = {
  id: string
  question: number
  answer_raw: string
  order: number
  question_row?: any
  question_column?: any
  text_value?: string
  response?: string
}

type Props = {
  children: React.ReactNode
  sectionId: string
  updateRepeatableAnswer: (
    sectionId: string,
    questionId: string,
    value: any
  ) => void
  sectionAnswers: RepeatSectionResponse[]
  originalPageId?: string
  sectionOrder?: number
}

function RepeatableSectionWrapper({
  children,
  sectionId,
  updateRepeatableAnswer,
  sectionAnswers,
  originalPageId,
  sectionOrder,
}: Props): React.ReactElement {
  const formContext = React.useContext(ZentakeFormContext)

  const wrappedContext = React.useMemo(() => {
    if (!formContext) return formContext

    const findAnswer = (questionId: number) =>
      sectionAnswers.find((a) => a.question === questionId)

    return {
      ...formContext,
      repeatSectionInfo: {
        pageId: sectionId,
        originalPageId: originalPageId || '',
        order: sectionOrder || 0,
      },
      getAnswer: (question: Question): Answer => {
        const answer = findAnswer(Number(question.id))
        return answer
          ? ({
              ...answer,
              answer_raw: answer.answer_raw ? String(answer.answer_raw) : '',
              answer_text: answer.answer_raw ? String(answer.answer_raw) : '',
              answer_files: [],
              response: '',
              question_column: null,
              question_row: null,
            } as Answer)
          : ({
              id: '',
              question: question.id,
              answer_raw: '',
              answer_text: '',
              answer_files: [],
              response: '',
              question_column: null,
              question_row: null,
            } as Answer)
      },

      isQuestionDirty: (question: Question): boolean => {
        const answer = findAnswer(Number(question.id))
        return answer !== undefined
      },

      isQuestionValid: (question: Question): boolean => {
        if (!question.is_required) return true

        const answer = findAnswer(Number(question.id))
        if (!answer?.answer_raw) return false

        const questionType = question.question_type?.question_type

        if (questionType === 'checkbox') {
          const choices = answer.answer_raw
            .split(',')
            .filter((c) => c.trim() !== '')
          return choices.length > 0
        }

        if (questionType === 'radio' || questionType === 'text') {
          return Boolean(answer.answer_raw.trim())
        }

        return Boolean(answer.answer_raw)
      },

      getQuestionError: (question: Question): string => {
        const isDirty = findAnswer(Number(question.id)) !== undefined

        if (isDirty && question.is_required) {
          const answer = findAnswer(Number(question.id))
          if (!answer?.answer_raw || answer.answer_raw.trim() === '') {
            return 'This field is required'
          }
        }
        return ''
      },

      getAnswerSignature: (question: Question): Answer => {
        const answer = findAnswer(Number(question.id))
        if (!answer) {
          return {
            id: '',
            question: question.id,
            answer_raw: '',
            answer_text: '',
            answer_files: [],
            response: '',
            question_column: null,
            question_row: null,
          } as Answer
        }

        return {
          ...answer,
          answer_raw: answer.answer_raw ? String(answer.answer_raw) : '',
          answer_text: answer.answer_raw ? String(answer.answer_raw) : '',
          answer_files: [],
          response: '',
          question_column: null,
          question_row: null,
        } as Answer
      },

      getAnswerCell: (
        question: Question,
        rowId?: any,
        columnId?: any
      ): Answer => {
        const answer = findAnswer(Number(question.id))
        return answer
          ? ({
              ...answer,
              answer_raw: answer.answer_raw ? String(answer.answer_raw) : '',
              answer_text: answer.answer_raw ? String(answer.answer_raw) : '',
              answer_files: [],
              response: '',
              question_column: columnId,
              question_row: rowId,
            } as Answer)
          : ({} as Answer)
      },

      setResponse: formContext.setResponse,

      onAnswerChanged: (question: Question, answer: Answer) => {
        let value = ''

        if (typeof answer === 'string') {
          value = answer
        } else if (typeof answer === 'object' && answer !== null) {
          const answerObj = answer as Record<string, any>

          if (answerObj.choiceId !== undefined) {
            if (
              answerObj.value !== undefined &&
              typeof answerObj.value === 'boolean'
            ) {
              const currentAnswer = findAnswer(Number(question.id))
              let currentChoices: string[] = []

              if (currentAnswer?.answer_raw) {
                currentChoices = currentAnswer.answer_raw
                  .split(',')
                  .filter((c) => c && typeof c === 'string' && c.trim() !== '')
              }

              const choiceIdStr = String(answerObj.choiceId || '')
              if (answerObj.value === true) {
                if (!currentChoices.includes(choiceIdStr)) {
                  currentChoices.push(choiceIdStr)
                }
              } else {
                currentChoices = currentChoices.filter((c) => c !== choiceIdStr)
              }

              value = currentChoices.join(',')
            } else {
              value = String(answerObj.choiceId || '')
            }
          } else {
            value = String(
              answer?.answer_raw ||
                answer?.answer_text ||
                answer?.text_value ||
                answer?.text ||
                ''
            )
          }
        } else if (answer) {
          value = String(answer)
        }

        updateRepeatableAnswer(sectionId, String(question.id), value)
      },

      onAnswerCellChanged: (question: Question, answer: Answer) => {
        const value =
          answer?.answer_raw || answer?.answer_text || answer?.text_value || ''

        updateRepeatableAnswer(sectionId, String(question.id), String(value))
      },

      onAnswerSignatureChanged: (question: Question, answer: Answer) => {
        const signatureData = {
          id: answer?.id || question.id,
          answer_raw: answer?.answer_raw || '',
          answer_text: answer?.answer_text || '',
        }

        const value = JSON.stringify(signatureData)
        updateRepeatableAnswer(sectionId, String(question.id), value)
      },
    }
  }, [
    formContext,
    sectionId,
    updateRepeatableAnswer,
    sectionAnswers,
    originalPageId,
    sectionOrder,
  ])

  return (
    <ZentakeFormContext.Provider value={wrappedContext}>
      {children}
    </ZentakeFormContext.Provider>
  )
}

export default RepeatableSectionWrapper
